import type { AiSuggestionReplacementOption, Suggestion } from '@tiptap-pro/extension-ai-suggestion';
import type { Editor } from '@tiptap/react';
import { getNextWord, getPreviousWord } from '@tiptap-pro/extension-ai-suggestion';
import { getHTMLFromFragment } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/era-ui';

type AISuggestionBubbleProps = {
  editor: Editor;
};

export const AISuggestionBubble = ({ editor }: AISuggestionBubbleProps) => {
  const shouldShowSuggestions = (): boolean => {
    const storage = editor.extensionStorage.aiSuggestion;
    if (!storage) {
      return false;
    }
    const suggestion = storage.getSelectedSuggestion?.();
    if (!suggestion?.deleteRange) {
      return false;
    }
    const { from, to } = suggestion.deleteRange;
    return from !== to;
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 100,
        zIndex: 9999,
        appendTo: () => document.body,
      }}
      className="flex flex-col gap-4 p-4 rounded-[12px] border border-divider-primary bg-white shadow-card min-w-[200px] z-[9999]"
      shouldShow={shouldShowSuggestions}
    >
      {(() => {
        const suggestion = editor.extensionStorage.aiSuggestion?.getSelectedSuggestion?.() as Suggestion | undefined;
        if (!suggestion) {
          return null;
        }
        return (
          <>
            {suggestion.replacementOptions.map((option: AiSuggestionReplacementOption, index: number) => {
              const { previousWord } = getPreviousWord(editor, suggestion.deleteRange.from);
              const { nextWord, punctuationMark } = getNextWord(editor, suggestion.deleteRange.to);

              return (
                <div key={index} className="space-y-4">
                  <p className="text-body-md">
                    {previousWord}
                    <span
                      className="mx-1 px-1 rounded text-body-md"
                      style={{
                        backgroundColor: suggestion.rule.backgroundColor,
                        color: suggestion.rule.color,
                      }}
                      // The following use of dangerouslySetInnerHTML is used for inserting AI suggested
                      // corrections (for spelling or grammar mistakes) so it's generally safe but
                      // just as a precaution added DOMPurify to sanitize the HTML.
                      // This is also the used method in the Tiptap docs (even though they don't even sanitize)
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(getHTMLFromFragment(option.addSlice.content, editor.schema)),
                      }}
                    />
                    {nextWord ? ` ${nextWord}...` : punctuationMark}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={async () => {
                        await editor.commands.applyAiSuggestion({
                          suggestionId: suggestion.id,
                          replacementOptionId: option.id,
                          format: 'rich-text',
                        });

                        // Manually reload suggestions
                        editor.commands.loadAiSuggestions();
                      }}
                      className="h-8 text-body-md rounded-lg"
                    >
                      Apply
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        editor.chain().rejectAiSuggestion(suggestion.id).focus().run();
                      }}
                      className="h-8 text-body-md"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              );
            })}
          </>
        );
      })()}
    </BubbleMenu>
  );
};
